import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Tooltip,
    useTheme,
} from '@mui/material';
import { Refresh, DeleteSweep, Replay, Storage, Memory, Search as SearchIcon, Dns } from '@mui/icons-material';
import api from '../../api/axios';

const POLL_MS = 15000;

interface Load {
    '1m': number;
    '5m': number;
    '15m': number;
}

interface ServerStats {
    disk: { total: number | null; free: number | null; used: number | null };
    uploads: {
        file_count: number;
        total_bytes: number;
        complaint_images: number;
        activity_attachments: number;
    };
    php: {
        version: string;
        upload_max_filesize: string;
        post_max_size: string;
        max_file_uploads: number;
        memory_limit: string;
        opcache: boolean;
    };
    app: {
        laravel_version: string;
        environment: string;
        debug: boolean;
        timezone: string;
        config_cached: boolean;
    };
    system: {
        load: Load | null;
        cpu_cores: number | null;
        memory: { total: number; available: number | null; used: number | null } | null;
        swap: { total: number; used: number | null } | null;
        uptime_seconds: number | null;
    };
    queue: {
        connection: string;
        pending: number;
        oldest_pending_seconds: number | null;
        worker_ok: boolean;
        failed_total: number;
        recent_failed: Array<{
            uuid: string;
            queue: string;
            connection: string;
            failed_at: string;
            error: string;
        }>;
    };
    search: {
        driver: string;
        host: string;
        queued: boolean;
        reachable: boolean;
        authenticated: boolean;
        indexes: number | null;
        documents: number | null;
        error: string | null;
    };
    database: {
        connection: string;
        name: string | null;
        version: string | null;
        size_bytes: number | null;
        error?: string;
    };
    generated_at: string;
}

type Tone = 'ok' | 'warning' | 'danger';

function formatBytes(b?: number | null): string {
    if (b === null || b === undefined || Number.isNaN(b)) return '—';
    if (b < 1024) return `${b} B`;
    const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
    let v = b / 1024;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function duration(seconds?: number | null): string {
    if (seconds === null || seconds === undefined) return '—';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}

export default function Server() {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [confirmFlush, setConfirmFlush] = useState(false);
    const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const { data, isLoading, isError, error, isFetching, dataUpdatedAt, refetch } = useQuery<ServerStats>({
        queryKey: ['server-stats'],
        queryFn: async () => {
            const res = await api.get('/superadmin/server');
            return res.data.data;
        },
        // The page is a live view of the box: poll every 15s. react-query pauses
        // this while the tab is in the background, so an open tab costs nothing.
        refetchInterval: POLL_MS,
        staleTime: 0,
    });

    // Drives the "updated Ns ago" counter between polls.
    const [, forceTick] = useState(0);
    useEffect(() => {
        const t = setInterval(() => forceTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, []);

    const toneColor = (tone: Tone): string =>
        tone === 'danger'
            ? theme.palette.error.main
            : tone === 'warning'
                ? theme.palette.warning.main
                : theme.palette.text.primary;

    const retryMutation = useMutation({
        mutationFn: () => api.post('/superadmin/server/queue/retry-failed'),
        onSuccess: () => {
            setToast({ open: true, message: 'Failed jobs queued for retry', severity: 'success' });
            queryClient.invalidateQueries({ queryKey: ['server-stats'] });
        },
        onError: () => setToast({ open: true, message: 'Could not retry failed jobs', severity: 'error' }),
    });

    const flushMutation = useMutation({
        mutationFn: () => api.post('/superadmin/server/queue/flush-failed'),
        onSuccess: () => {
            setConfirmFlush(false);
            setToast({ open: true, message: 'Failed jobs cleared', severity: 'success' });
            queryClient.invalidateQueries({ queryKey: ['server-stats'] });
        },
        onError: () => {
            setConfirmFlush(false);
            setToast({ open: true, message: 'Could not clear failed jobs', severity: 'error' });
        },
    });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !data) {
        return (
            <Box>
                <Typography variant="h4" gutterBottom>Server</Typography>
                <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>
                    Could not load server stats{error instanceof Error ? `: ${error.message}` : '.'}
                </Alert>
            </Box>
        );
    }

    const { disk, uploads, php, app, system, queue, search, database } = data;
    const mem = system.memory;
    const swap = system.swap;

    const diskPct = disk.total && disk.used !== null ? Math.min(100, (disk.used / disk.total) * 100) : null;
    const diskTone: Tone = diskPct === null ? 'ok' : diskPct >= 90 ? 'danger' : diskPct >= 75 ? 'warning' : 'ok';

    const memPct = mem?.total && mem.used !== null ? Math.min(100, (mem.used / mem.total) * 100) : null;
    const memTone: Tone = memPct === null ? 'ok' : memPct >= 90 ? 'danger' : memPct >= 75 ? 'warning' : 'ok';

    const swapPct = swap?.total && swap.used !== null ? Math.min(100, (swap.used / swap.total) * 100) : null;
    // Heavy swap use on a small VM signals RAM pressure even when "available" looks fine.
    const swapTone: Tone = swapPct === null ? 'ok' : swapPct >= 50 ? 'danger' : swapPct >= 20 ? 'warning' : 'ok';

    // Load relative to core count: above 1.0 per core the CPU is saturated.
    const loadRatio = system.load && system.cpu_cores ? system.load['1m'] / system.cpu_cores : null;
    const loadTone: Tone = loadRatio === null ? 'ok' : loadRatio >= 1 ? 'danger' : loadRatio >= 0.7 ? 'warning' : 'ok';

    const secondsAgo = Math.max(0, Math.round((Date.now() - dataUpdatedAt) / 1000));

    const Stat = ({ label, value, tone = 'ok' }: { label: string; value: React.ReactNode; tone?: Tone }) => (
        <Box>
            <Typography
                sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
                color="text.secondary"
            >
                {label}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5, mt: 0.25, color: toneColor(tone) }}>
                {value ?? '—'}
            </Typography>
        </Box>
    );

    const KeyVal = ({ label, value }: { label: string; value: React.ReactNode }) => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                py: 0.75,
                borderBottom: `1px dashed ${theme.palette.divider}`,
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right' }}>
                {value ?? '—'}
            </Typography>
        </Box>
    );

    const Bar = ({ pct, tone }: { pct: number; tone: Tone }) => (
        <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: theme.palette.action.hover,
                '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor:
                        tone === 'danger'
                            ? theme.palette.error.main
                            : tone === 'warning'
                                ? theme.palette.warning.main
                                : theme.palette.success.main,
                },
            }}
        />
    );

    const statGrid = {
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
    } as const;

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4">Server</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Host resources, background queue and search engine
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Tooltip title={`Auto-refreshing every ${POLL_MS / 1000} seconds`}>
                        <Chip
                            size="small"
                            color={isFetching ? 'primary' : 'default'}
                            variant={isFetching ? 'filled' : 'outlined'}
                            label={isFetching ? 'Refreshing…' : `Updated ${secondsAgo}s ago`}
                        />
                    </Tooltip>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => refetch()}
                        disabled={isFetching}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* CPU & memory */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Memory fontSize="small" color="action" />
                            <Typography variant="h6">CPU &amp; memory</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {system.cpu_cores ? `· ${system.cpu_cores} vCPU` : ''}
                                {system.uptime_seconds ? ` · up ${duration(system.uptime_seconds)}` : ''}
                            </Typography>
                        </Box>

                        <Box sx={statGrid}>
                            <Stat label="Load (1m)" value={system.load ? system.load['1m'] : '—'} tone={loadTone} />
                            <Stat
                                label="Load 5m / 15m"
                                value={system.load ? `${system.load['5m']} / ${system.load['15m']}` : '—'}
                            />
                            <Stat
                                label="RAM used"
                                value={mem ? `${formatBytes(mem.used)} / ${formatBytes(mem.total)}` : '—'}
                                tone={memTone}
                            />
                            <Stat
                                label="Swap used"
                                value={swap ? `${formatBytes(swap.used)} / ${formatBytes(swap.total)}` : '—'}
                                tone={swapTone}
                            />
                        </Box>

                        {memPct !== null && (
                            <Box sx={{ mt: 2.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    RAM ({memPct.toFixed(0)}%)
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Bar pct={memPct} tone={memTone} />
                                </Box>
                            </Box>
                        )}

                        {/* A few hundred MB of swap with RAM free is normal Linux behaviour
                            (cold pages), so stay quiet until it is genuinely high. */}
                        {swapPct !== null && swapPct >= 20 && (
                            <Alert severity={swapTone === 'danger' ? 'error' : 'warning'} sx={{ mt: 2 }}>
                                Swap at {swapPct.toFixed(0)}% — the box is leaning on swap, a sign it is short on RAM.
                                If this stays high, size the VM up.
                            </Alert>
                        )}
                        {loadTone === 'danger' && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Load exceeds the CPU core count — the server is saturated. Sustained high load means it
                                is time to size up.
                            </Alert>
                        )}
                        {!system.load && !mem && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                Host metrics unavailable (non-Linux host).
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Background queue */}
                <Card>
                    <CardContent>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                            }}
                        >
                            <Box>
                                <Typography variant="h6">Background queue</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Connection: {queue.connection || '—'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Replay />}
                                    disabled={!queue.failed_total || retryMutation.isPending}
                                    onClick={() => retryMutation.mutate()}
                                >
                                    Retry failed
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteSweep />}
                                    disabled={!queue.failed_total}
                                    onClick={() => setConfirmFlush(true)}
                                >
                                    Clear failed
                                </Button>
                            </Box>
                        </Box>

                        <Box sx={statGrid}>
                            <Box>
                                <Typography
                                    sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
                                    color="text.secondary"
                                >
                                    Worker
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 22,
                                        fontWeight: 700,
                                        letterSpacing: -0.5,
                                        mt: 0.25,
                                        color: queue.worker_ok ? theme.palette.success.main : theme.palette.error.main,
                                    }}
                                >
                                    {queue.worker_ok ? 'Healthy' : 'Check'}
                                </Typography>
                            </Box>
                            <Stat label="Pending jobs" value={queue.pending.toLocaleString()} />
                            <Stat label="Oldest waiting" value={duration(queue.oldest_pending_seconds)} />
                            <Stat
                                label="Failed jobs"
                                value={queue.failed_total.toLocaleString()}
                                tone={queue.failed_total ? 'danger' : 'ok'}
                            />
                        </Box>

                        {!queue.worker_ok && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Jobs are piling up and not draining — the queue worker may be down. On the server check{' '}
                                <code>systemctl status &apos;leader360-queue@*&apos;</code>.
                                if it is stopped, restart it with:
                                <code> systemctl start leader360-queue@1.service
                                    systemctl start leader360-queue@2.service</code>
                            </Alert>
                        )}

                        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                            Recent failures ({queue.failed_total.toLocaleString()})
                        </Typography>
                        {!queue.recent_failed.length ? (
                            <Typography variant="caption" color="text.secondary">
                                No failed jobs.
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Failed at</TableCell>
                                            <TableCell>Queue</TableCell>
                                            <TableCell>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {queue.recent_failed.map((j) => (
                                            <TableRow key={j.uuid}>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{j.failed_at}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                    {j.queue}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                    {j.error}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>

                {/* Search engine */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <SearchIcon fontSize="small" color="action" />
                            <Typography variant="h6">Search engine</Typography>
                            <Chip
                                size="small"
                                label={
                                    search.reachable && search.authenticated
                                        ? 'Operational'
                                        : search.reachable
                                            ? 'Auth failed'
                                            : 'Unreachable'
                                }
                                color={search.reachable && search.authenticated ? 'success' : 'error'}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={statGrid}>
                            <Stat label="Driver" value={search.driver || '—'} />
                            <Stat label="Indexes" value={search.indexes ?? '—'} />
                            <Stat label="Documents" value={search.documents?.toLocaleString() ?? '—'} />
                            <Stat label="Queued indexing" value={search.queued ? 'On' : 'Off'} />
                        </Box>
                        {search.error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {search.error} Indexing jobs will fail while this is broken — search results go stale
                                even though the rest of the app keeps working.
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                {/* Disk */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Storage fontSize="small" color="action" />
                            <Typography variant="h6">Disk usage</Typography>
                            <Typography variant="body2" color="text.secondary">
                                · partition hosting app storage
                            </Typography>
                        </Box>
                        {diskPct !== null && (
                            <Box sx={{ mb: 2 }}>
                                <Bar pct={diskPct} tone={diskTone} />
                            </Box>
                        )}
                        <Box sx={statGrid}>
                            <Stat label="Total" value={formatBytes(disk.total)} />
                            <Stat label="Used" value={formatBytes(disk.used)} />
                            <Stat label="Free" value={formatBytes(disk.free)} tone={diskTone} />
                            <Stat
                                label="% Used"
                                value={diskPct === null ? '—' : `${diskPct.toFixed(1)}%`}
                                tone={diskTone}
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Uploads + runtime */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1.5 }}>
                                Uploads
                            </Typography>
                            <KeyVal label="files stored" value={uploads.file_count.toLocaleString()} />
                            <KeyVal label="total size" value={formatBytes(uploads.total_bytes)} />
                            <KeyVal label="request images" value={uploads.complaint_images.toLocaleString()} />
                            <KeyVal label="activity attachments" value={uploads.activity_attachments.toLocaleString()} />
                            <KeyVal
                                label="share of disk"
                                value={
                                    disk.total ? `${((uploads.total_bytes / disk.total) * 100).toFixed(2)}%` : '—'
                                }
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                Totals refresh every minute.
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Dns fontSize="small" color="action" />
                                <Typography variant="h6">Runtime</Typography>
                            </Box>
                            <KeyVal label="PHP" value={php.version} />
                            <KeyVal label="Laravel" value={app.laravel_version} />
                            <KeyVal
                                label="environment"
                                value={
                                    <Chip
                                        size="small"
                                        label={app.environment}
                                        color={app.environment === 'production' ? 'success' : 'warning'}
                                        variant="outlined"
                                    />
                                }
                            />
                            <KeyVal
                                label="debug mode"
                                value={
                                    app.debug ? (
                                        <Chip size="small" label="ON" color="error" variant="outlined" />
                                    ) : (
                                        'off'
                                    )
                                }
                            />
                            <KeyVal label="config cached" value={app.config_cached ? 'yes' : 'no'} />
                            <KeyVal label="opcache" value={php.opcache ? 'enabled' : 'disabled'} />
                            <KeyVal label="timezone" value={app.timezone} />
                            <KeyVal label="upload_max_filesize" value={php.upload_max_filesize} />
                            <KeyVal label="post_max_size" value={php.post_max_size} />
                            <KeyVal label="memory_limit" value={php.memory_limit} />
                            <KeyVal label="database" value={`${database.name ?? '—'} · ${formatBytes(database.size_bytes)}`} />
                            <KeyVal label="mysql" value={database.version ?? '—'} />
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Clear failed jobs confirmation */}
            <Dialog open={confirmFlush} onClose={() => setConfirmFlush(false)}>
                <DialogTitle>Clear failed jobs</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Permanently delete all {queue.failed_total.toLocaleString()} failed job records? This cannot be
                        undone, and the work in them will not be retried.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmFlush(false)}>Cancel</Button>
                    <Button color="error" disabled={flushMutation.isPending} onClick={() => flushMutation.mutate()}>
                        Clear
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
